import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import Fab from '@mui/material/Fab';
import LinearProgress from '@mui/material/LinearProgress';
import CircularProgress from '@mui/material/CircularProgress';
import Button from '@mui/material/Button';
import AddIcon from '@mui/icons-material/Add';
import PaymentsIcon from '@mui/icons-material/Payments';
import FoundationIcon from '@mui/icons-material/Foundation';
import SavingsIcon from '@mui/icons-material/Savings';
import PlaceIcon from '@mui/icons-material/Place';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';
import StatCard from '../components/StatCard';
import NewProjectDialog from '../components/NewProjectDialog';
import WorkerProfileCard from '../components/WorkerProfileCard';
import useLocation from '../hooks/useLocation';
import { formatMoney } from '../components/Money';

const ProjectCard = ({ project, onClick }) => {
  const { t } = useTranslation();
  const spent = Number(project.total_spent) || 0;
  const budget = Number(project.budget_total) || 0;
  const pct = budget ? Math.min(100, Math.round((spent / budget) * 100)) : null;

  return (
    <Card>
      <CardActionArea onClick={onClick}>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle1" noWrap>
                {project.name}
              </Typography>
              {project.site_address && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                >
                  <PlaceIcon sx={{ fontSize: 14 }} /> {project.site_address}
                </Typography>
              )}
            </Box>
            <Chip
              size="small"
              label={project.status === 'active' ? t('project.active') : t('project.completed')}
              color={project.status === 'active' ? 'success' : 'default'}
              variant="outlined"
            />
          </Stack>

          <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mt: 1.5 }}>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              {formatMoney(spent)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t('home.spent')}
              {budget ? ` · ${formatMoney(budget)} ${t('home.budget')}` : ''}
            </Typography>
          </Stack>
          {pct !== null && (
            <LinearProgress
              variant="determinate"
              value={pct}
              color={pct > 90 ? 'error' : 'primary'}
              sx={{ mt: 1, height: 6, borderRadius: 3 }}
            />
          )}
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

const Home = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { capture } = useLocation();
  const [data, setData] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const load = async () => {
    const res = await api.get('/api/dashboard');
    setData(res.data);
  };

  useEffect(() => {
    load().catch(() => setData({ projects: [], projectCount: 0, activeCount: 0, totalSpent: 0, totalBudget: 0 }));
    /* capture device location once per session (best-effort, silent) */
    if (!sessionStorage.getItem('bp-located')) {
      sessionStorage.setItem('bp-located', '1');
      capture();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!data) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  const isConstructor = user?.role === 'constructor';
  const emptyText =
    user?.role === 'client'
      ? t('home.clientNoProjects')
      : user?.role === 'worker'
        ? t('home.workerNoProjects')
        : t('home.noProjectsHint');

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 2 }}>
        {user?.name ? `👋 ${user.name}` : t('nav.home')}
      </Typography>

      {user?.role === 'worker' && <WorkerProfileCard />}

      {isConstructor && (
        <Button
          fullWidth
          variant="outlined"
          startIcon={<PersonSearchIcon />}
          onClick={() => navigate('/find-workers')}
          sx={{ mb: 2 }}
        >
          {t('nearby.findWorkers')}
        </Button>
      )}

      {user?.role === 'worker' ? (
        <>
          <Typography variant="subtitle1" sx={{ mb: 1.5 }}>
            {t('worker.myEarnings')}
          </Typography>
          <Stack direction="row" spacing={1.5} sx={{ mb: 3 }}>
            <StatCard
              label={t('worker.earned')}
              value={formatMoney(data.workerStats?.earned)}
              icon={<PaymentsIcon />}
              color="success.main"
            />
            <StatCard
              label={t('worker.received')}
              value={formatMoney(data.workerStats?.paid)}
              icon={<SavingsIcon />}
            />
            <StatCard
              label={t('worker.balance')}
              value={formatMoney(Math.abs(data.workerStats?.balance || 0))}
              icon={<FoundationIcon />}
              color={(data.workerStats?.balance || 0) > 0 ? 'error.main' : 'secondary.main'}
            />
          </Stack>
        </>
      ) : (
        <Stack direction="row" spacing={1.5} sx={{ mb: 3 }}>
          <StatCard
            label={t('home.totalSpent')}
            value={formatMoney(data.totalSpent)}
            icon={<PaymentsIcon />}
          />
          <StatCard
            label={t('home.activeProjects')}
            value={data.activeCount}
            icon={<FoundationIcon />}
            color="secondary.main"
          />
          {data.totalBudget > 0 && (
            <StatCard
              label={t('home.totalBudget')}
              value={formatMoney(data.totalBudget)}
              icon={<SavingsIcon />}
              color="success.main"
            />
          )}
        </Stack>
      )}

      <Typography variant="subtitle1" sx={{ mb: 1.5 }}>
        {t('home.myProjects')}
      </Typography>

      {data.projects.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="subtitle1">{t('home.noProjects')}</Typography>
            <Typography variant="body2" color="text.secondary">
              {emptyText}
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Stack spacing={1.5}>
          {data.projects.map((p) => (
            <ProjectCard key={p.id} project={p} onClick={() => navigate(`/projects/${p.id}`)} />
          ))}
        </Stack>
      )}

      {isConstructor && (
        <>
          <Fab
            color="primary"
            variant="extended"
            onClick={() => setDialogOpen(true)}
            sx={{ position: 'fixed', bottom: 24, right: 24 }}
          >
            <AddIcon sx={{ mr: 0.5 }} /> {t('home.newProject')}
          </Fab>
          <NewProjectDialog
            open={dialogOpen}
            onClose={() => setDialogOpen(false)}
            onCreated={(project) => {
              setDialogOpen(false);
              navigate(`/projects/${project.id}`);
            }}
          />
        </>
      )}
    </Box>
  );
};

export default Home;
